import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Loader2, Phone, Wrench } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";
import { useHoneypot } from "../components/Honeypot";
import { MarketingOptIn } from "../components/MarketingOptIn";
import { trackEnquiryConversion } from "../lib/analytics";
import { BUILD_NOTES, BUILD_OPTION_GROUPS } from "../data/buildOptions";

const STEPS = ["The vehicle", "The plan", "What to quote", "Your details"];

const field =
  "w-full rounded-lg border bg-transparent px-3.5 py-2.5 text-sm text-[color:var(--nq-sand)] outline-none transition-colors duration-200 placeholder:text-[color:var(--nq-stone)]/60 focus:border-[color:var(--nq-gold)]";
const fieldStyle = { borderColor: "rgba(255,255,255,0.12)" };

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-[color:var(--nq-stone)]">
    {children}
  </label>
);

export default function NewVehicleBuild() {
  useSeo({
    title: "New Vehicle Build Enquiry - NQ Springs & 4x4 Accessories",
    description:
      "Kitting out a new 4WD? Tick the mods you want quoted - suspension, GVM, bar work, touring gear, trays - and NQ Springs Townsville will build your rig from start to finish.",
    canonical: "/new-vehicle-build",
  });

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const { honeypot, HoneypotField } = useHoneypot();
  const [optIn, setOptIn] = useState(false);
  const [form, setForm] = useState({
    make: "", model: "", year: "", variant: "",
    intended_use: "", budget: "",
    notes: "", name: "", phone: "", email: "",
  });
  const [picked, setPicked] = useState([]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (label) =>
    setPicked((p) => (p.includes(label) ? p.filter((x) => x !== label) : [...p, label]));

  const stepValid = useMemo(() => {
    if (step === 0) return form.make.trim() && form.model.trim();
    if (step === 3) return form.name.trim() && form.phone.trim() && /\S+@\S+\.\S+/.test(form.email);
    return true;
  }, [step, form]);

  const submit = async (e) => {
    e.preventDefault();
    if (!stepValid) return;
    setBusy(true);
    try {
      const { data } = await api.post("/build-enquiries", {
        ...form,
        options: picked,
        website: honeypot,
        marketing_opt_in: optIn,
      });
      setDone(data.reference);
      trackEnquiryConversion({ source: "new_vehicle_build", email: form.email, phone: form.phone });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send that - please ring us on (07) 4774 8859.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="container-nq section-nq" data-testid="build-enquiry-success">
        <div className="mx-auto max-w-xl text-center">
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-full"
            style={{ background: "var(--nq-gold)" }}
          >
            <Check className="h-7 w-7" style={{ color: "var(--nq-black)" }} />
          </div>
          <h1 className="mt-6 text-3xl font-bold md:text-4xl">Build sheet received</h1>
          <p className="mt-3 text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
            Your reference is <span className="gold-text font-mono font-semibold">{done}</span>. It has
            landed in the build sales inbox and we'll be in touch to talk through every option on your
            list. If you'd rather talk it out now, ring Oscar on{" "}
            <a href="tel:0747748859" className="gold-text font-semibold">(07) 4774 8859</a>.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/our-builds" className="btn-gold">See our builds</Link>
            <Link to="/" className="btn-outline-nq">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="new-vehicle-build-page">
      {/* ---------------- intro ---------------- */}
      <section className="border-b" style={{ borderColor: "rgba(255,255,255,0.08)", background: "var(--nq-ink)" }}>
        <div className="container-nq py-12 md:py-16">
          <p className="eyebrow">New vehicle build</p>
          <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-[1.08] md:text-5xl">
            Are you looking to <span className="gold-text">kit out your rig?</span>
          </h1>
          <div className="mt-5 max-w-2xl space-y-3 text-sm leading-relaxed text-[color:var(--nq-stone)] md:text-base">
            <p>
              Looking for your one stop shop to get all your mods done? Go through the options below to
              organise your new build - tick everything you want quoted and we'll take it from there.
            </p>
            <p>
              Questions? Send them to{" "}
              <a href="mailto:sales4@nqsprings.com.au" className="gold-text font-semibold">sales4@nqsprings.com.au</a>.
              Not sure what to buy? Ring <strong>Oscar</strong> on{" "}
              <a href="tel:0747748859" className="gold-text font-semibold">(07) 4774 8859</a> and he'll take you
              through the pros and cons of each model and help you settle on your new pride and joy.
            </p>
          </div>
        </div>
      </section>

      <div className="container-nq section-nq">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* ---------------- the wizard ---------------- */}
          <div className="lg:col-span-8">
            {/* progress */}
            <ol className="flex flex-wrap gap-x-2 gap-y-2" data-testid="build-steps">
              {STEPS.map((label, i) => (
                <li key={label} className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => i < step && setStep(i)}
                    className="flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-200"
                    style={{
                      background: i === step ? "var(--nq-gold)" : "rgba(255,255,255,0.04)",
                      color: i === step ? "var(--nq-black)" : "var(--nq-stone)",
                      cursor: i < step ? "pointer" : "default",
                    }}
                    data-testid={`build-step-${i}`}
                  >
                    <span className="font-mono">{i + 1}</span> {label}
                  </button>
                </li>
              ))}
            </ol>

            <form onSubmit={submit} className="card-nq mt-5 p-5 md:p-7" data-testid="build-form">
              {HoneypotField}

              {step === 0 && (
                <div className="grid gap-4 sm:grid-cols-2" data-testid="build-step-vehicle">
                  <div>
                    <Label htmlFor="make">Vehicle make *</Label>
                    <input id="make" required value={form.make} onChange={set("make")} placeholder="Toyota, Ford, Ram..." className={field} style={fieldStyle} data-testid="build-make" />
                  </div>
                  <div>
                    <Label htmlFor="model">Vehicle model *</Label>
                    <input id="model" required value={form.model} onChange={set("model")} placeholder="LandCruiser 79, Ranger, 1500..." className={field} style={fieldStyle} data-testid="build-model" />
                  </div>
                  <div>
                    <Label htmlFor="year">Year built</Label>
                    <input id="year" value={form.year} onChange={set("year")} placeholder="2025" className={field} style={fieldStyle} data-testid="build-year" />
                  </div>
                  <div>
                    <Label htmlFor="variant">Variant / sub model</Label>
                    <input id="variant" value={form.variant} onChange={set("variant")} placeholder="GXL, Wildtrak, Laramie..." className={field} style={fieldStyle} data-testid="build-variant" />
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-5" data-testid="build-step-plan">
                  <div>
                    <Label htmlFor="use">What do you plan on doing with the vehicle?</Label>
                    <textarea
                      id="use" rows={5} value={form.intended_use} onChange={set("intended_use")}
                      placeholder="Work car, full on touring, weekend camping, hard wheeling..."
                      className={field} style={fieldStyle} data-testid="build-use"
                    />
                  </div>
                  <div className="sm:max-w-xs">
                    <Label htmlFor="budget">Rough budget in mind</Label>
                    <input id="budget" value={form.budget} onChange={set("budget")} placeholder="$25,000" className={field} style={fieldStyle} data-testid="build-budget" />
                    <p className="mt-2 text-xs text-[color:var(--nq-stone)]">
                      A ballpark is plenty - it just helps us spec the right gear first time.
                    </p>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="build-step-options">
                  <p className="text-sm text-[color:var(--nq-stone)]">
                    Tick anything you'd like quoted. Nothing here is a commitment.
                  </p>
                  <div className="mt-5 space-y-6">
                    {BUILD_OPTION_GROUPS.map((group) => (
                      <div key={group.title}>
                        <p className="text-xs font-semibold uppercase tracking-[0.12em] gold-text">{group.title}</p>
                        <div className="mt-2.5 grid gap-2 sm:grid-cols-2">
                          {group.items.map(([label, note]) => {
                            const on = picked.includes(label);
                            return (
                              <label
                                key={label}
                                className="flex cursor-pointer items-start gap-2.5 rounded-lg border p-3 text-sm transition-colors duration-200"
                                style={{
                                  borderColor: on ? "rgba(198,162,74,0.5)" : "rgba(255,255,255,0.08)",
                                  background: on ? "rgba(198,162,74,0.08)" : "rgba(255,255,255,0.02)",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={on}
                                  onChange={() => toggle(label)}
                                  className="mt-0.5 h-4 w-4 shrink-0 accent-[color:var(--nq-gold)]"
                                  data-testid={`build-option-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "")}`}
                                />
                                <span>
                                  <span className="font-medium text-[color:var(--nq-sand)]">{label}</span>
                                  {note && (
                                    <span className="mt-0.5 block text-xs text-[color:var(--nq-stone)]">{note}</span>
                                  )}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6">
                    <Label htmlFor="notes">Anything else you're after?</Label>
                    <textarea
                      id="notes" rows={4} value={form.notes} onChange={set("notes")}
                      placeholder="Tell us what else you want on it, or what the dual battery has to run..."
                      className={field} style={fieldStyle} data-testid="build-notes"
                    />
                    <p className="mt-2 text-xs text-[color:var(--nq-stone)]">
                      If it's not on the list, ask anyway - we'll see if we can accommodate it.
                    </p>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="grid gap-4 sm:grid-cols-2" data-testid="build-step-details">
                  <div className="sm:col-span-2">
                    <Label htmlFor="name">Your name *</Label>
                    <input id="name" required value={form.name} onChange={set("name")} className={field} style={fieldStyle} data-testid="build-name" />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone number *</Label>
                    <input id="phone" required value={form.phone} onChange={set("phone")} className={field} style={fieldStyle} data-testid="build-phone" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <input id="email" type="email" required value={form.email} onChange={set("email")} className={field} style={fieldStyle} data-testid="build-email" />
                  </div>
                  <p className="sm:col-span-2 text-xs text-[color:var(--nq-stone)]">
                    {picked.length} item{picked.length === 1 ? "" : "s"} ticked for quoting. We'll email you
                    a copy of nothing you didn't ask for - this goes straight to our build desk.
                  </p>
                </div>
              )}

              {step === STEPS.length - 1 && (
                <MarketingOptIn id="build-marketing-opt-in" className="mt-6" checked={optIn} onChange={setOptIn} />
              )}

              {/* nav */}
              <div className="mt-7 flex flex-wrap items-center gap-3 border-t pt-5" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                {step > 0 && (
                  <button type="button" onClick={() => setStep((s) => s - 1)} className="btn-outline-nq" data-testid="build-back">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                )}
                {step < STEPS.length - 1 ? (
                  <button
                    type="button"
                    onClick={() => stepValid && setStep((s) => s + 1)}
                    disabled={!stepValid}
                    className="btn-gold disabled:opacity-50"
                    data-testid="build-next"
                  >
                    Next <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button type="submit" disabled={busy || !stepValid} className="btn-gold disabled:opacity-50" data-testid="build-submit">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
                    {busy ? "Sending..." : "Send build sheet"}
                  </button>
                )}
                <span className="text-xs text-[color:var(--nq-stone)]">
                  Step {step + 1} of {STEPS.length}
                </span>
              </div>
            </form>
          </div>

          {/* ---------------- side notes ---------------- */}
          <aside className="lg:col-span-4">
            <div className="card-nq p-5" data-testid="build-notes-panel">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] gold-text">Before you start</p>
              <ul className="mt-3 space-y-2.5">
                {BUILD_NOTES.map((n) => (
                  <li key={n} className="flex gap-2 text-[13px] leading-relaxed text-[color:var(--nq-stone)]">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full" style={{ background: "var(--nq-green-bright)" }} />
                    {n}
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="mt-4 rounded-xl border p-5"
              style={{ borderColor: "rgba(198,162,74,0.28)", background: "rgba(198,162,74,0.06)" }}
            >
              <p className="text-sm font-semibold">Rather talk it through?</p>
              <p className="mt-1 text-[13px] leading-relaxed text-[color:var(--nq-stone)]">
                Oscar will run you through the pros and cons of each model before you spend a cent.
              </p>
              <a href="tel:0747748859" className="btn-gold mt-4 w-full justify-center" data-testid="build-call-oscar">
                <Phone className="h-4 w-4" /> (07) 4774 8859
              </a>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
